// /admin/rules/new and /admin/rules/:ruleId/edit — Rule wizard (v6 IA).
//
// Three-step wizard backed by the same v0.2 rule shape (group_match +
// two-axis scope; deny-wins composition). Mirrors the
// `mockups/access-preview-v6.html` `data-view="wizard"` markup. Live
// header sentence + step pips are click-to-jump; the validate() debounce
// powers the review panel shown on the Role step.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError, api, useApiErrorHandler } from "@/api";
import { Button, Modal, useToast } from "@/components";
import { ruleClient } from "@/data/clients/ruleClient";
import { roleClient } from "@/data/clients/roleClient";
import { permissionClient } from "@/data/clients/permissionClient";
import { locationsClient } from "@/api/locationsClient";
import { AdminApiError } from "@/data/httpClient";
import { useCapabilities } from "@/role";
import type { Role, PermissionCatalog } from "@/types/role";
import { permissionDisplayName } from "@/types/role";
import type {
  DeviceScope,
  LocationScope,
  MatchCondition,
  MatchJoin,
  MatchOp,
  ScopeMode,
  ValidateRuleRequest,
} from "@/types/rule";
import {
  MAX_SCOPE_IDS_PER_RULE,
  MAX_MATCH_CONDITIONS_PER_AXIS,
  MATCH_VALUE_MAX_LENGTH,
} from "@/types/rule";
import {
  buildDeviceScope,
  highlightName,
  isDeviceScopeSaveable,
  isOverMatchContains,
  isPredicateSelector,
  MATCH_OPS,
  newCondition,
  selectorFromDeviceScope,
  validatePredicateConditions,
  withCid,
  type DeviceSelector,
} from "./devicePredicate";
import type { PreviewDeviceSummary } from "@/data/clients/ruleClient";
import type { Location } from "@/types/location";
import "../AdminShared.css";
import "./RuleForm.css";

// ---------------------------------------------------------------------------
// Types — minimal, local-only. Group list comes from the BFF's /api/groups
// endpoint; we tolerate either {items} or {groups} envelopes since
// neither has a typed client today.
// ---------------------------------------------------------------------------

interface GroupSummary {
  group_id: string;
  group_name: string;
  member_count: number;
  rules_count: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function adaptGroup(raw: any): GroupSummary {
  return {
    group_id: String(raw.group_id ?? raw.id ?? raw.group_name ?? ""),
    group_name: String(raw.group_name ?? raw.name ?? raw.group_id ?? ""),
    member_count: Number(raw.member_count ?? raw.members_count ?? raw.size ?? 0),
    rules_count: Number(raw.rules_count ?? raw.rule_count ?? 0),
  };
}

interface GroupsResp {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groups?: any[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Lowercase + hyphenated slug — used for the default rule_id. */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

/** Map a role to one of the v6-role-chip color classes. */
function roleClass(role: Role): string {
  const id = role.role_id.toLowerCase();
  if (id === "fleet_admin") return "r-fleet";
  if (id === "tenant_admin") return "r-tenant";
  if (id === "regional_admin") return "r-regional";
  if (id.includes("manager")) return "r-manager";
  if (id.includes("viewer")) return "r-viewer";
  if (id.includes("investigator")) return "r-investigator";
  if (role.is_system) return "r-manager";
  return "r-custom";
}

/** Stable display label for the role chip — uppercase canonical id. */
function roleChipLabel(role: Role): string {
  return role.role_id.toUpperCase();
}

interface WizardStateInit {
  group_name?: string;
  role_id?: string;
  display_name?: string;
  description?: string;
  rule_id?: string;
}

type StepNum = 1 | 2 | 3 | 4;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RuleFormPage(): JSX.Element {
  const { ruleId } = useParams<{ ruleId: string }>();
  const isEdit = Boolean(ruleId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handleApiError = useApiErrorHandler();
  const toast = useToast();
  const caps = useCapabilities();
  const canManage = caps.canManageRules;

  // Wizard state ------------------------------------------------------------
  const [step, setStep] = useState<StepNum>(1);

  // Step 1
  const [groupName, setGroupName] = useState<string>(
    searchParams.get("group") ?? "",
  );
  const [groupSearch, setGroupSearch] = useState<string>("");
  const [formRuleId, setFormRuleId] = useState<string>("");
  const [ruleIdEdited, setRuleIdEdited] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  // Step 2
  const [locMode, setLocMode] = useState<ScopeMode>("ALL");
  const [locIds, setLocIds] = useState<string[]>([]);
  // Device axis (LLD 12): a five-way selector that maps 1:1 to the engine's
  // orthogonal (mode, selector) model. ALL / ids / predicate.
  const [devSelector, setDevSelector] = useState<DeviceSelector>("ALL");
  const [devIds, setDevIds] = useState<string[]>([]);
  // Predicate builder state (only meaningful for the two *_MATCH selectors).
  const [devConditions, setDevConditions] = useState<MatchCondition[]>([
    newCondition(),
  ]);
  const [devJoin, setDevJoin] = useState<MatchJoin>("ALL_OF");
  // Required dynamic-set / renamable-name acknowledgement before saving any
  // predicate device scope (LLD 12 §4.4).
  const [devPredicateAck, setDevPredicateAck] = useState<boolean>(false);

  // Step 3
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [showCustomRoleModal, setShowCustomRoleModal] = useState<boolean>(false);

  // Backing data -----------------------------------------------------------
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [groupsLoading, setGroupsLoading] = useState<boolean>(true);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);

  // Edit-mode bookkeeping
  const [originalVersion, setOriginalVersion] = useState<number>(0);
  const [loadingRule, setLoadingRule] = useState<boolean>(isEdit);

  // Submit / validate state
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [validateResp, setValidateResp] = useState<{
    ok: boolean;
    errors?: string[];
  } | null>(null);
  const [validating, setValidating] = useState<boolean>(false);
  // Affected-user preview: how many aliases would receive the role if saved.
  // null = unknown / preview unavailable; we hide the count rather than block.
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  // Device match summary from the live preview, present only for a predicate
  // device scope (LLD 12 §4.4). null = no predicate / unavailable.
  const [devicePreview, setDevicePreview] = useState<PreviewDeviceSummary | null>(
    null,
  );
  // True from the instant a predicate edit lands until the next preview result
  // resolves — drives the "checking…" affordance so it's clear a background
  // device scan is in flight while the admin is still typing. Flipped
  // synchronously (not behind the debounce) so feedback is immediate.
  const [devicePreviewPending, setDevicePreviewPending] = useState<boolean>(false);

  // -----------------------------------------------------------------------
  // Initial loads
  // -----------------------------------------------------------------------

  useEffect(() => {
    void (async () => {
      setGroupsLoading(true);
      try {
        const resp = await api.get<GroupsResp>("/api/groups");
        const list = (resp.items ?? resp.groups ?? []).map(adaptGroup);
        setGroups(list);
      } catch (err) {
        if (err instanceof ApiError) {
          handleApiError(err, { actionLabel: "Load groups" });
        }
      } finally {
        setGroupsLoading(false);
      }
    })();
  }, [handleApiError]);

  useEffect(() => {
    void (async () => {
      setRolesLoading(true);
      try {
        const resp = await roleClient.list({ limit: 200 });
        setRoles(resp.items.filter((r) => !r.deleted_at));
      } catch (err) {
        if (err instanceof ApiError) {
          handleApiError(err, { actionLabel: "Load roles" });
        }
      } finally {
        setRolesLoading(false);
      }
    })();
  }, [handleApiError]);

  useEffect(() => {
    void (async () => {
      try {
        const list = await locationsClient.list();
        setLocations(list);
      } catch {
        // Non-fatal — chip picker just won't suggest anything.
      }
    })();
    void (async () => {
      try {
        const c = await permissionClient.getCatalog();
        setCatalog(c);
      } catch {
        // Non-fatal — perm-list bullets fall back to raw keys.
      }
    })();
  }, []);

  // Load existing rule ------------------------------------------------------
  useEffect(() => {
    if (!isEdit || !ruleId) return;
    void (async () => {
      setLoadingRule(true);
      try {
        const data = await ruleClient.get(ruleId);
        const init: WizardStateInit = {
          group_name: data.group_match?.group_name ?? "",
          role_id: data.role_id,
          display_name: data.display_name,
          description: data.description,
          rule_id: data.rule_id,
        };
        setGroupName(init.group_name ?? "");
        setSelectedRoleId(init.role_id ?? "");
        setDisplayName(init.display_name ?? "");
        setDescription(init.description ?? "");
        setFormRuleId(init.rule_id ?? "");
        setRuleIdEdited(true); // never auto-clobber rule_id on edit
        setLocMode(data.location_scope.mode);
        setLocIds([...(data.location_scope.ids ?? [])]);
        const sel = selectorFromDeviceScope(data.device_scope);
        setDevSelector(sel);
        setDevIds([...(data.device_scope.ids ?? [])]);
        if (data.device_scope.predicate) {
          const pred = data.device_scope.predicate;
          setDevConditions(
            pred.conditions.length > 0
              ? pred.conditions.map(withCid)
              : [newCondition()],
          );
          setDevJoin(pred.join ?? "ALL_OF");
          // An existing predicate rule was already acknowledged when saved.
          setDevPredicateAck(true);
        }
        setOriginalVersion(data.version);
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 404) {
            navigate("/admin/people", { replace: true });
            toast.danger({ title: "Rule not found" });
          } else {
            handleApiError(err, { actionLabel: `Load rule ${ruleId}` });
          }
        }
      } finally {
        setLoadingRule(false);
      }
    })();
  }, [isEdit, ruleId, navigate, toast, handleApiError]);

  // Auto-suggest rule_id (only on new + while the user hasn't hand-edited it).
  // Prefer the display name when present; otherwise fall back to the chosen
  // group so the Step 4 rule_id field is pre-filled even before a display
  // name is typed.
  useEffect(() => {
    if (isEdit) return;
    if (ruleIdEdited) return;
    const seed = displayName.trim() || groupName.trim();
    setFormRuleId(slugify(seed));
  }, [displayName, groupName, isEdit, ruleIdEdited]);

  // -----------------------------------------------------------------------
  // Derived values
  // -----------------------------------------------------------------------

  const sortedRoles = useMemo(
    () =>
      [...roles]
        .filter((r) => r.role_id !== "tenant_admin")
        .sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [roles],
  );

  const selectedRole = useMemo(
    () => sortedRoles.find((r) => r.role_id === selectedRoleId) ?? null,
    [sortedRoles, selectedRoleId],
  );

  const groupsByName = useMemo(() => {
    const m = new Map<string, GroupSummary>();
    for (const g of groups) m.set(g.group_name, g);
    return m;
  }, [groups]);

  const locationsByCloudId = useMemo(() => {
    const m = new Map<string, Location>();
    for (const l of locations) m.set(l.ring_location_id, l);
    return m;
  }, [locations]);

  const filteredGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    const filtered = q.length === 0
      ? groups
      : groups.filter(
          (g) =>
            g.group_name.toLowerCase().includes(q) ||
            g.group_id.toLowerCase().includes(q),
        );
    // Unrouted (rules_count === 0) first, then alpha by name.
    return [...filtered].sort((a, b) => {
      if (a.rules_count === 0 && b.rules_count !== 0) return -1;
      if (a.rules_count !== 0 && b.rules_count === 0) return 1;
      return a.group_name.localeCompare(b.group_name);
    });
  }, [groups, groupSearch]);

  // Permission key → friendly label, fed from the catalog. Falls back to the
  // raw key when the catalog hasn't loaded yet.
  const permLabelFor = useCallback(
    (key: string): string => {
      if (!catalog) return key;
      for (const grp of catalog.groups) {
        for (const p of grp.permissions) {
          if (p.key === key) return permissionDisplayName(p);
        }
      }
      return key;
    },
    [catalog],
  );

  // "where" sentence summary
  const whereSummary = useMemo(() => {
    if (locMode === "ALL") return "all locations";
    if (locIds.length === 0) {
      return locMode === "ALLOW" ? "(no locations)" : "all locations";
    }
    if (locIds.length === 1) {
      const id = locIds[0];
      if (id === undefined) return "all locations";
      const loc = locationsByCloudId.get(id);
      return loc ? loc.display_name : id;
    }
    return `${locIds.length} locations`;
  }, [locMode, locIds, locationsByCloudId]);

  // Step validity (new order: 1 Members, 2 Role, 3 Cameras, 4 Review)
  const step1Valid = groupName.trim().length > 0;
  const deviceScopeSaveable = isDeviceScopeSaveable(
    devSelector,
    devIds,
    devConditions,
    MAX_SCOPE_IDS_PER_RULE,
    devPredicateAck,
  );
  // Step 2 gates on a role being chosen; Step 3 on scope/location validity.
  const step2Valid = selectedRoleId.length > 0;
  const step3Valid =
    !(locMode !== "ALL" && locIds.length === 0) &&
    locIds.length <= MAX_SCOPE_IDS_PER_RULE &&
    deviceScopeSaveable;
  // Step 4 — name & describe. rule_id is required (auto-derived on new,
  // disabled & preserved on edit); display name and description are optional.
  const step4Valid = formRuleId.trim().length > 0;

  const canJumpTo = useCallback(
    (target: StepNum): boolean => {
      if (target === 1) return true;
      if (target === 2) return step1Valid;
      if (target === 3) return step1Valid && step2Valid;
      if (target === 4) return step1Valid && step2Valid && step3Valid;
      return false;
    },
    [step1Valid, step2Valid, step3Valid],
  );

  const goTo = useCallback(
    (target: StepNum): void => {
      if (canJumpTo(target)) setStep(target);
    },
    [canJumpTo],
  );

  // -----------------------------------------------------------------------
  // Validate (debounced) — drives the Step 3 review panel
  // -----------------------------------------------------------------------

  const validateBody: ValidateRuleRequest = useMemo(
    () => ({
      rule_id: formRuleId.trim() || undefined,
      display_name: displayName.trim() || undefined,
      description: description.trim() || undefined,
      group_match: groupName.trim()
        ? { type: "exact", group_name: groupName.trim() }
        : undefined,
      role_id: selectedRoleId || undefined,
      location_scope: { mode: locMode, ids: locMode === "ALL" ? [] : locIds },
      device_scope: buildDeviceScope(devSelector, devIds, devConditions, devJoin),
      enabled: true,
    }),
    [
      formRuleId,
      displayName,
      description,
      groupName,
      selectedRoleId,
      locMode,
      locIds,
      devSelector,
      devIds,
      devConditions,
      devJoin,
    ],
  );

  // A predicate device scope whose conditions are currently valid enough to be
  // worth a backend scan — gates the "checking…" affordance so it never shows
  // for an id-only/ALL scope or a blank/garbled predicate.
  const devicePreviewActive = useMemo(
    () =>
      isPredicateSelector(devSelector) &&
      validatePredicateConditions(devConditions).length === 0,
    [devSelector, devConditions],
  );

  // Review-panel validation body — deliberately omits device_scope. The Review
  // checks (group exists, role, conflicts) don't depend on the camera scope, and
  // the predicate is already validated client-side (devicePreviewActive) and
  // again server-side inside preview + at save. Omitting it means editing the
  // name predicate does NOT re-fire validate — only the preview below runs.
  const reviewValidateBody: ValidateRuleRequest = useMemo(
    () => ({
      rule_id: formRuleId.trim() || undefined,
      group_match: groupName.trim()
        ? { type: "exact", group_name: groupName.trim() }
        : undefined,
      role_id: selectedRoleId || undefined,
      location_scope: { mode: locMode, ids: locMode === "ALL" ? [] : locIds },
    }),
    [formRuleId, groupName, selectedRoleId, locMode, locIds],
  );

  // Effect 1 — validate, for the Review panel. Fires on group/role/location
  // edits, NOT on device-predicate edits.
  const validateTimerRef = useRef<number | null>(null);
  useEffect(() => {
    // Only validate once we have a group + role; the validate endpoint
    // 400s on empty bodies and the wizard hasn't asked for them yet.
    if (!groupName.trim() || !selectedRoleId) {
      setValidateResp(null);
      return;
    }
    if (validateTimerRef.current !== null) {
      window.clearTimeout(validateTimerRef.current);
    }
    setValidating(true);
    validateTimerRef.current = window.setTimeout(() => {
      void (async () => {
        try {
          setValidateResp(await ruleClient.validate(reviewValidateBody));
        } catch {
          // Non-fatal — review panel falls back to client-side checks.
          setValidateResp(null);
        } finally {
          setValidating(false);
        }
      })();
    }, 300);
    return () => {
      if (validateTimerRef.current !== null) {
        window.clearTimeout(validateTimerRef.current);
      }
    };
  }, [reviewValidateBody, groupName, selectedRoleId]);

  // Effect 2 — preview, for the device match set + affected-user count. Calls
  // preview directly (no validate gate): preview validates server-side and 400s
  // cleanly on a bad rule, which we swallow. So typing predicate text triggers
  // exactly ONE backend call, not a serial validate→preview pair.
  const previewTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!groupName.trim() || !selectedRoleId) {
      setPreviewCount(null);
      setDevicePreview(null);
      setDevicePreviewPending(false);
      return;
    }
    if (previewTimerRef.current !== null) {
      window.clearTimeout(previewTimerRef.current);
    }
    // Flag the device preview as in-flight the moment a predicate edit lands,
    // BEFORE the debounce — so the "checking…" affordance shows immediately
    // while the admin is still typing, not 300ms later.
    if (devicePreviewActive) {
      setDevicePreviewPending(true);
    }
    previewTimerRef.current = window.setTimeout(() => {
      void (async () => {
        try {
          const preview = await ruleClient.preview(validateBody);
          setPreviewCount(preview.summary.affected_alias_count);
          setDevicePreview(preview.device_preview ?? null);
        } catch {
          // Best-effort UX — hide the count/preview, never block submit.
          setPreviewCount(null);
          setDevicePreview(null);
        } finally {
          setDevicePreviewPending(false);
        }
      })();
    }, 300);
    return () => {
      if (previewTimerRef.current !== null) {
        window.clearTimeout(previewTimerRef.current);
      }
    };
  }, [validateBody, groupName, selectedRoleId, devicePreviewActive]);

  // -----------------------------------------------------------------------
  // Save
  // -----------------------------------------------------------------------

  const handleSave = useCallback(async (): Promise<void> => {
    if (!step1Valid || !step2Valid || !step3Valid || !step4Valid) return;
    setSubmitting(true);
    try {
      const ruleBody = {
        display_name: displayName.trim() || groupName.trim() + " → " + selectedRoleId,
        description: description.trim(),
        group_match: { type: "exact" as const, group_name: groupName.trim() },
        role_id: selectedRoleId,
        location_scope: {
          mode: locMode,
          ids: locMode === "ALL" ? [] : locIds,
        } satisfies LocationScope,
        device_scope: buildDeviceScope(
          devSelector,
          devIds,
          devConditions,
          devJoin,
        ) satisfies DeviceScope,
        enabled: true,
      };
      if (isEdit && ruleId) {
        const result = await ruleClient.update(ruleId, ruleBody, originalVersion);
        const totalGrants =
          (result.summary?.grants_added ?? 0) +
          (result.summary?.grants_removed ?? 0);
        if (result.partial && result.partial.length > 0) {
          const succeeded =
            totalGrants - (result.summary?.failures_count ?? 0);
          toast.warning({
            title: `Rule '${ruleBody.display_name}' updated`,
            description: `${succeeded} of ${totalGrants} reconciles succeeded. ${result.summary?.failures_count ?? 0} failed.`,
          });
        } else {
          toast.success({
            title: `Rule '${ruleBody.display_name}' updated`,
            description:
              totalGrants > 0 ? `${totalGrants} grants reconciled.` : undefined,
          });
        }
        navigate(`/admin/people/groups/${encodeURIComponent(groupName.trim())}`);
      } else {
        const result = await ruleClient.create({
          rule_id: formRuleId.trim() || slugify(ruleBody.display_name) || "rule",
          ...ruleBody,
        });
        const grantsAdded = result.summary?.grants_added ?? 0;
        toast.success({
          title: `Rule '${ruleBody.display_name}' created`,
          description:
            grantsAdded > 0 ? `${grantsAdded} grants added.` : undefined,
        });
        navigate(`/admin/people/groups/${encodeURIComponent(groupName.trim())}`);
      }
    } catch (err) {
      if (err instanceof AdminApiError) {
        if (err.typed.kind === "version_mismatch") {
          toast.warning({
            title: "Version conflict",
            description:
              "Someone else edited this rule. Please reload and try again.",
          });
        } else {
          toast.danger({ title: err.message });
        }
      } else if (err instanceof ApiError) {
        if (err.status === 409) {
          toast.danger({
            title: "Rule ID already exists",
            description: "Choose a different rule ID.",
          });
        } else {
          handleApiError(err, {
            actionLabel: isEdit ? "Update rule" : "Create rule",
          });
        }
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    isEdit,
    ruleId,
    step1Valid,
    step2Valid,
    step3Valid,
    step4Valid,
    formRuleId,
    displayName,
    description,
    groupName,
    selectedRoleId,
    locMode,
    locIds,
    devSelector,
    devIds,
    devConditions,
    devJoin,
    originalVersion,
    navigate,
    toast,
    handleApiError,
  ]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!canManage) {
    return (
      <section className="admin-page">
        <div className="admin-empty">
          You need <code>rule:manage</code> to create or edit rules.
        </div>
      </section>
    );
  }

  if (loadingRule || rolesLoading) {
    return (
      <section className="admin-page">
        <div className="admin-empty">Loading…</div>
      </section>
    );
  }

  // Build live-sentence slot props.
  const groupSlot = groupName.trim();
  const roleSlot = selectedRole ? roleChipLabel(selectedRole) : "";

  return (
    <section className="admin-page">
      <header className="page-header">
        <div className="rule-builder__breadcrumb">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/admin/people");
            }}
          >
            ← Groups &amp; users
          </a>
        </div>
        <h1 className="page-title">
          {isEdit ? "Edit rule" : "Grant a group access"}
        </h1>
        <p className="page-subtitle">
          Four quick steps. The plain-English sentence at the top updates as you go.
        </p>
      </header>

      <div className="v6-wizard-frame">
        {/* Live sentence */}
        <div className="v6-wizard-header">
          <div className="v6-wizard-sentence">
            Members of{" "}
            <button
              type="button"
              className={`slot ${groupSlot ? "filled" : "empty"}`}
              onClick={() => goTo(1)}
              aria-label="Edit group selection"
            >
              {groupSlot || "group"}
            </button>
            <span className="glue">get the</span>
            <button
              type="button"
              className={`slot ${roleSlot ? "filled" : "empty"}`}
              onClick={() => goTo(2)}
              aria-label="Edit role selection"
              disabled={!canJumpTo(2)}
            >
              {roleSlot || "role"}
            </button>
            <span className="glue">at</span>
            <button
              type="button"
              className={`slot ${locMode === "ALL" || locIds.length > 0 ? "filled" : "empty"}`}
              onClick={() => goTo(3)}
              aria-label="Edit scope selection"
              disabled={!canJumpTo(3)}
            >
              {whereSummary}
            </button>
            <span className="glue">.</span>
          </div>
        </div>

        {/* Step pips */}
        <div className="v6-wizard-steps">
          <StepPip
            n={1}
            label="Members"
            current={step}
            jumpable={canJumpTo(1)}
            onClick={() => goTo(1)}
          />
          <span className="v6-step-divider" />
          <StepPip
            n={2}
            label="Role"
            current={step}
            jumpable={canJumpTo(2)}
            onClick={() => goTo(2)}
          />
          <span className="v6-step-divider" />
          <StepPip
            n={3}
            label="Cameras"
            current={step}
            jumpable={canJumpTo(3)}
            onClick={() => goTo(3)}
          />
          <span className="v6-step-divider" />
          <StepPip
            n={4}
            label="Review"
            current={step}
            jumpable={canJumpTo(4)}
            onClick={() => goTo(4)}
          />
        </div>

        {/* Body */}
        {step === 1 && (
          <div className="v6-wizard-body">
            <h3 className="v6-wizard-question">Which group should get access?</h3>
            <p className="v6-wizard-helper">
              Groups are synced from your IdP via SCIM. Adding members happens in your IdP — you only choose what role they get here.
            </p>

            <div className="v6-step-search-wrap">
              <span className="ico" aria-hidden>⌕</span>
              <input
                type="search"
                placeholder="Search groups by name or ID…"
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                aria-label="Search groups"
              />
            </div>

            <div className="v6-group-pick-list">
              {groupsLoading ? (
                <div className="admin-empty">Loading groups…</div>
              ) : filteredGroups.length === 0 ? (
                <div className="admin-empty">
                  {groupSearch.trim()
                    ? `No groups match "${groupSearch.trim()}".`
                    : "No SCIM groups have synced yet."}
                </div>
              ) : (
                filteredGroups.map((g) => {
                  const selected = g.group_name === groupName;
                  const ruleNote =
                    g.rules_count === 0
                      ? "no rules yet"
                      : g.rules_count === 1
                        ? "1 rule"
                        : `${g.rules_count} rules`;
                  return (
                    <label
                      key={g.group_id || g.group_name}
                      className={`v6-group-pick-row${selected ? " selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="wizard-group"
                        checked={selected}
                        onChange={() => setGroupName(g.group_name)}
                      />
                      <div className="gpr-radio" />
                      <div className="gpr-meta">
                        <div className="gpr-name">
                          {g.group_name}
                          {g.rules_count === 0 && (
                            <span className="gpr-tag">unrouted</span>
                          )}
                        </div>
                        <div className="gpr-id">
                          {g.group_id} · {g.member_count} members · {ruleNote}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="v6-wizard-body">
            <h3 className="v6-wizard-question">Where should this access apply?</h3>
            <p className="v6-wizard-helper">
              Pick locations first. By default the rule covers every camera at the chosen locations — narrow further only if you need to.
            </p>

            <ScopeSection
              title="Locations"
              summary={
                locMode === "ALL"
                  ? `All ${locations.length} locations`
                  : `${locIds.length} location${locIds.length === 1 ? "" : "s"} selected`
              }
              mode={locMode}
              ids={locIds}
              onModeChange={(m) => {
                setLocMode(m);
                if (m === "ALL") setLocIds([]);
              }}
              onIdsChange={setLocIds}
              modeLabels={{
                ALL: ["All locations", "Every site in this workspace, including new ones added later."],
                ALLOW: ["Only specific locations", "Pick exact sites. New sites won't be auto-included."],
                DENY: ["Everywhere except…", "Allow all, except a chosen list. Useful for sandboxes."],
              }}
              directory={locations.map((l) => ({
                id: l.ring_location_id,
                label: l.display_name,
              }))}
              noun="location"
            />

            <DeviceScopeSection
              selector={devSelector}
              ids={devIds}
              conditions={devConditions}
              join={devJoin}
              acknowledged={devPredicateAck}
              devicePreview={devicePreview}
              previewPending={devicePreviewPending}
              onSelectorChange={(s) => {
                setDevSelector(s);
                if (s === "ALL") setDevIds([]);
                // Reset the predicate ack whenever the selector changes so the
                // admin must re-acknowledge the dynamic-set semantics.
                if (!isPredicateSelector(s)) setDevPredicateAck(false);
              }}
              onIdsChange={setDevIds}
              onConditionsChange={setDevConditions}
              onJoinChange={setDevJoin}
              onAcknowledgedChange={setDevPredicateAck}
            />
          </div>
        )}

        {step === 2 && (
          <div className="v6-wizard-body">
            <h3 className="v6-wizard-question">What should they be able to do?</h3>
            <p className="v6-wizard-helper">
              Pick a role. The chip color and ✓ list show exactly what they can do — and what they can't.
            </p>

            <div className="v6-role-grid">
              {sortedRoles.map((r) => {
                const selected = r.role_id === selectedRoleId;
                const top4 = r.permission_set.slice(0, 4);
                return (
                  <label
                    key={r.role_id}
                    className={`v6-role-card${selected ? " selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="wizard-role"
                      checked={selected}
                      onChange={() => setSelectedRoleId(r.role_id)}
                      style={{ position: "absolute", opacity: 0 }}
                    />
                    <div className="rc-head">
                      <span className={`v6-role-chip ${roleClass(r)}`}>
                        {roleChipLabel(r)}
                      </span>
                      <span className="rc-name">{r.display_name}</span>
                    </div>
                    {r.description && <p className="rc-desc">{r.description}</p>}
                    <ul className="perm-list">
                      {top4.map((key) => (
                        <li key={key}>{permLabelFor(key)}</li>
                      ))}
                    </ul>
                  </label>
                );
              })}
              <a
                className="v6-role-card role-build-custom"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowCustomRoleModal(true);
                }}
              >
                <div className="rc-head">
                  <span className="rc-name">+ Build a custom role</span>
                </div>
                <p className="rc-desc">
                  Pick exact permissions. Used when none of the templates fit.
                </p>
              </a>
            </div>

            <ReviewBlock
              groupName={groupName}
              groupSummary={groupsByName.get(groupName)}
              role={selectedRole}
              roleSlot={roleSlot}
              whereSummary={whereSummary}
              locationsCount={locations.length}
              locMode={locMode}
              locIds={locIds}
              validateResp={validateResp}
              validating={validating}
              groupExists={groupsByName.has(groupName)}
            />
          </div>
        )}

        {step === 4 && (
          <div className="v6-wizard-body">
            <h3 className="v6-wizard-question">Name and describe this rule</h3>
            <p className="v6-wizard-helper">
              Give this rule a stable ID and a friendly name so it&apos;s easy to
              find later. The display name and description are optional — we&apos;ll
              fill in a sensible default if you leave them blank.
            </p>

            <div className="rule-wiz__adv-fields">
              <label className="rule-wiz__adv-label">
                Rule ID
                <span className="rule-wiz__adv-hint">
                  {" "}— shown in audit log; lowercase, hyphens
                </span>
              </label>
              <input
                className="rule-wiz__adv-input mono"
                type="text"
                value={formRuleId}
                onChange={(e) => {
                  setRuleIdEdited(true);
                  setFormRuleId(e.target.value);
                }}
                disabled={isEdit}
                placeholder="e.g. fleet-admins-everywhere"
                aria-label="Rule ID"
              />
              <label className="rule-wiz__adv-label">
                Display name <span className="rule-wiz__adv-hint">— optional</span>
              </label>
              <input
                className="rule-wiz__adv-input"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Fleet Admins everywhere"
                aria-label="Display name"
              />
              <label className="rule-wiz__adv-label">
                Description <span className="rule-wiz__adv-hint">— optional</span>
              </label>
              <textarea
                className="rule-wiz__adv-input"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Why does this rule exist? Who owns it?"
                aria-label="Description"
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="v6-wizard-footer">
          <div className="footer-left">Step {step} of 4</div>
          <div className="footer-right">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/people")}
              disabled={submitting}
            >
              Cancel
            </Button>
            {step > 1 && (
              <Button
                variant="secondary"
                onClick={() => setStep(((step - 1) as StepNum))}
                disabled={submitting}
              >
                ← Back
              </Button>
            )}
            {step < 4 && (
              <Button
                variant="primary"
                onClick={() => {
                  const next = (step + 1) as StepNum;
                  if (canJumpTo(next)) setStep(next);
                }}
                disabled={
                  (step === 1 && !step1Valid) ||
                  (step === 2 && !step2Valid) ||
                  (step === 3 && !step3Valid)
                }
              >
                Continue →
              </Button>
            )}
            {step === 4 && previewCount !== null && (
              <span className="rule-form-preview-count" aria-live="polite">
                {previewCount === 1
                  ? "1 user will receive this role"
                  : `${previewCount} users will receive this role`}
              </span>
            )}
            {step === 4 && (
              <Button
                variant="primary"
                loading={submitting}
                disabled={
                  !step1Valid ||
                  !step2Valid ||
                  !step3Valid ||
                  !step4Valid ||
                  submitting
                }
                onClick={() => void handleSave()}
              >
                Save rule
              </Button>
            )}
          </div>
        </div>
      </div>

      {showCustomRoleModal && (
        <CustomRoleQuickModal
          catalog={catalog}
          onClose={() => setShowCustomRoleModal(false)}
          onCreated={(created) => {
            setRoles((prev) => [...prev, created]);
            setSelectedRoleId(created.role_id);
            setShowCustomRoleModal(false);
            toast.success({
              title: `Role '${created.display_name}' created`,
            });
          }}
          onError={(err) => {
            if (err instanceof ApiError) {
              handleApiError(err, { actionLabel: "Create custom role" });
            } else {
              toast.danger({ title: "Failed to create role" });
            }
          }}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// <StepPip>
// ---------------------------------------------------------------------------

interface StepPipProps {
  n: StepNum;
  label: string;
  current: StepNum;
  jumpable: boolean;
  onClick: () => void;
}

function StepPip(props: StepPipProps): JSX.Element {
  const { n, label, current, jumpable, onClick } = props;
  const cls =
    n === current ? "v6-step-pip active" : n < current ? "v6-step-pip done" : "v6-step-pip";
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      disabled={!jumpable}
      aria-current={n === current ? "step" : undefined}
    >
      <span className="num">{n}</span>
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// <ScopeSection> — locations card with mode + chip picker
// ---------------------------------------------------------------------------

interface DirEntry {
  id: string;
  label: string;
}

interface ScopeSectionProps {
  title: string;
  summary: string;
  mode: ScopeMode;
  ids: string[];
  onModeChange: (m: ScopeMode) => void;
  onIdsChange: (ids: string[]) => void;
  modeLabels: Record<ScopeMode, [string, string]>;
  directory: DirEntry[];
  noun: string;
}

function ScopeSection(props: ScopeSectionProps): JSX.Element {
  const {
    title,
    summary,
    mode,
    ids,
    onModeChange,
    onIdsChange,
    modeLabels,
    directory,
    noun,
  } = props;

  const labelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of directory) m.set(d.id, d.label);
    return m;
  }, [directory]);

  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!picking) return;
    const h = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        e.target instanceof Node &&
        !containerRef.current.contains(e.target)
      ) {
        setPicking(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [picking]);

  const remaining = useMemo(() => {
    const sel = new Set(ids);
    const q = query.trim().toLowerCase();
    return directory
      .filter((d) => !sel.has(d.id))
      .filter(
        (d) =>
          q.length === 0 ||
          d.label.toLowerCase().includes(q) ||
          d.id.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [directory, ids, query]);

  return (
    <div className="v6-scope-section">
      <div className="v6-scope-section-head">
        <span className="scope-title">{title}</span>
        <span className="scope-summary">{summary}</span>
      </div>
      <ScopeModeRow
        mode={mode}
        onChange={onModeChange}
        modeLabels={modeLabels}
        name={`${noun}-mode`}
      />
      {mode !== "ALL" && (
        <div className="v6-scope-chips" ref={containerRef}>
          {ids.map((id) => {
            const label = labelMap.get(id) ?? id;
            return (
              <span key={id} className="v6-scope-chip">
                {label}
                <button
                  type="button"
                  className="x"
                  aria-label={`Remove ${label}`}
                  onClick={() => onIdsChange(ids.filter((x) => x !== id))}
                >
                  ×
                </button>
              </span>
            );
          })}
          <button
            type="button"
            className="v6-scope-chip-add"
            onClick={() => setPicking((p) => !p)}
          >
            + Add {noun}
          </button>
          {picking && (
            <div className="rule-wiz__chip-popover">
              <input
                className="rule-wiz__adv-input"
                type="search"
                placeholder={`Search ${noun}s…`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
              <ul className="rule-wiz__chip-list">
                {remaining.length === 0 && (
                  <li className="rule-wiz__chip-empty">
                    {query.trim()
                      ? `No ${noun}s match "${query.trim()}".`
                      : `All ${noun}s already selected.`}
                  </li>
                )}
                {remaining.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => {
                        if (ids.length >= MAX_SCOPE_IDS_PER_RULE) return;
                        onIdsChange([...ids, d.id]);
                        setQuery("");
                      }}
                    >
                      <span>{d.label}</span>
                      {d.label !== d.id && <code className="mono">{d.id}</code>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ScopeModeRowProps {
  mode: ScopeMode;
  onChange: (m: ScopeMode) => void;
  modeLabels: Record<ScopeMode, [string, string]>;
  name: string;
}

function ScopeModeRow(props: ScopeModeRowProps): JSX.Element {
  const { mode, onChange, modeLabels, name } = props;
  const modes: ScopeMode[] = ["ALL", "ALLOW", "DENY"];
  return (
    <div className="v6-scope-mode-row">
      {modes.map((m) => {
        const labels = modeLabels[m];
        return (
          <label
            key={m}
            className={`v6-scope-mode${mode === m ? " selected" : ""}`}
          >
            <input
              type="radio"
              name={name}
              checked={mode === m}
              onChange={() => onChange(m)}
              style={{ position: "absolute", opacity: 0 }}
            />
            <div className="sm-radio" />
            <div className="sm-text">
              <b>{labels[0]}</b>
              <span>{labels[1]}</span>
            </div>
          </label>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <DeviceScopeSection> — LLD 12 device axis: five selectors mapping 1:1 to the
// engine, with a flat predicate condition builder for the two *_MATCH options.
// ---------------------------------------------------------------------------

interface DeviceScopeSectionProps {
  selector: DeviceSelector;
  ids: string[];
  conditions: MatchCondition[];
  join: MatchJoin;
  acknowledged: boolean;
  devicePreview: PreviewDeviceSummary | null;
  previewPending: boolean;
  onSelectorChange: (s: DeviceSelector) => void;
  onIdsChange: (ids: string[]) => void;
  onConditionsChange: (c: MatchCondition[]) => void;
  onJoinChange: (j: MatchJoin) => void;
  onAcknowledgedChange: (v: boolean) => void;
}

const DEVICE_SELECTORS: { value: DeviceSelector; title: string; hint: string }[] = [
  { value: "ALL", title: "All cameras", hint: "(default) Every camera at the chosen locations." },
  { value: "ALLOW_IDS", title: "Only these cameras", hint: "Pick an explicit list of camera IDs." },
  { value: "DENY_IDS", title: "All except these", hint: "Allow most, exclude a chosen list of IDs." },
  {
    value: "ALLOW_MATCH",
    title: "Devices matching…",
    hint: "Allow cameras whose name matches a condition.",
  },
  {
    value: "DENY_MATCH",
    title: "All except those matching…",
    hint: "Deny cameras whose name matches — the recommended way to keep a role off the X cameras.",
  },
];

function DeviceScopeSection(props: DeviceScopeSectionProps): JSX.Element {
  const {
    selector,
    ids,
    conditions,
    join,
    acknowledged,
    devicePreview,
    previewPending,
    onSelectorChange,
    onIdsChange,
    onConditionsChange,
    onJoinChange,
    onAcknowledgedChange,
  } = props;

  const predicateMode = isPredicateSelector(selector);
  const summary =
    selector === "ALL"
      ? "All cameras at chosen locations"
      : predicateMode
        ? `${conditions.length} name condition${conditions.length === 1 ? "" : "s"}`
        : `${ids.length} camera${ids.length === 1 ? "" : "s"} selected`;

  const updateCondition = (i: number, patch: Partial<MatchCondition>): void => {
    onConditionsChange(
      conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    );
  };
  const addCondition = (): void => {
    if (conditions.length >= MAX_MATCH_CONDITIONS_PER_AXIS) return;
    onConditionsChange([...conditions, newCondition()]);
  };
  const removeCondition = (i: number): void => {
    if (conditions.length <= 1) return;
    onConditionsChange(conditions.filter((_, idx) => idx !== i));
  };

  const predicateErrors = predicateMode
    ? validatePredicateConditions(conditions)
    : [];

  return (
    <details className="v6-scope-section" open>
      <summary
        className="v6-scope-section-head"
        style={{ cursor: "pointer" }}
      >
        <span className="scope-title">
          Devices{" "}
          <span className="rule-wiz__adv-hint">— optional, advanced</span>
        </span>
        <span className="scope-summary">{summary}</span>
      </summary>
      <p className="v6-wizard-helper" style={{ marginTop: 8 }}>
        By default this rule covers all cameras at the chosen locations. Narrow
        only if you have a real reason — e.g. a contractor who can see the
        parking-lot cam but not the warehouse floor.
      </p>

      <div className="v6-scope-mode-row" role="radiogroup" aria-label="Device scope">
        {DEVICE_SELECTORS.map((s) => (
          <label
            key={s.value}
            className={`v6-scope-mode${selector === s.value ? " selected" : ""}`}
          >
            <input
              type="radio"
              name="dev-selector"
              checked={selector === s.value}
              onChange={() => onSelectorChange(s.value)}
              style={{ position: "absolute", opacity: 0 }}
            />
            <div className="sm-radio" />
            <div className="sm-text">
              <b>{s.title}</b>
              <span>{s.hint}</span>
            </div>
          </label>
        ))}
      </div>

      {/* ids selector */}
      {(selector === "ALLOW_IDS" || selector === "DENY_IDS") && (
        <>
          <p className="v6-wizard-helper" style={{ marginTop: 10 }}>
            Camera scope is by ID — paste comma-separated camera IDs below.
          </p>
          <textarea
            className="rule-wiz__adv-input"
            rows={2}
            value={ids.join(", ")}
            onChange={(e) => {
              const next = e.target.value
                .split(/[,\s]+/)
                .map((str) => str.trim())
                .filter((str) => str.length > 0);
              onIdsChange(next.slice(0, MAX_SCOPE_IDS_PER_RULE));
            }}
            placeholder="doorbot-1234, doorbot-5678"
            aria-label="Camera IDs"
          />
        </>
      )}

      {/* predicate selector — condition builder */}
      {predicateMode && (
        <div className="rule-wiz__predicate" style={{ marginTop: 10 }}>
          <p className="v6-wizard-helper">
            Matches on each camera&apos;s current display name.
          </p>

          <div className="cond-joinbar">
            <span className="cond-joinbar__lbl">Join</span>
            <div
              className="cond-seg"
              role="radiogroup"
              aria-label="How conditions combine"
            >
              <button
                type="button"
                role="radio"
                aria-checked={join === "ALL_OF"}
                className={`cond-seg__btn${join === "ALL_OF" ? " on" : ""}`}
                onClick={() => onJoinChange("ALL_OF")}
              >
                Match all · AND
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={join === "ANY_OF"}
                className={`cond-seg__btn${join === "ANY_OF" ? " on" : ""}`}
                onClick={() => onJoinChange("ANY_OF")}
              >
                Match any · OR
              </button>
            </div>
            <span className="cond-joinbar__hint">
              {conditions.length > 1
                ? "how multiple conditions combine"
                : "applies once you add a second condition"}
            </span>
          </div>

          <div className="cond-list">
            {conditions.map((c, i) => {
              const overMatch = isOverMatchContains(c);
              return (
                <div key={c.cid ?? i} className="cond-row">
                  <span className="cond-row__lead">name</span>
                  <select
                    className="cond-row__op"
                    aria-label="Operator"
                    value={c.op}
                    onChange={(e) =>
                      updateCondition(i, { op: e.target.value as MatchOp })
                    }
                  >
                    {MATCH_OPS.map((o) => (
                      <option key={o.op} value={o.op}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <span className="cond-row__value-wrap">
                    <input
                      className="cond-row__value"
                      type="text"
                      aria-label="Value"
                      value={c.value}
                      maxLength={MATCH_VALUE_MAX_LENGTH}
                      onChange={(e) => updateCondition(i, { value: e.target.value })}
                      placeholder="e.g. Break Room"
                    />
                    {previewPending && (
                      <span
                        className="cond-spinner cond-row__value-spinner"
                        aria-hidden="true"
                        title="Checking matching cameras…"
                      />
                    )}
                  </span>
                  <label className="cond-row__flag" title="Negate this condition (does NOT match)">
                    <input
                      type="checkbox"
                      aria-label="does not"
                      checked={c.negate ?? false}
                      onChange={(e) => updateCondition(i, { negate: e.target.checked })}
                    />
                    does not
                  </label>
                  <label className="cond-row__flag" title="Case-insensitive match">
                    <input
                      type="checkbox"
                      aria-label="case insensitive"
                      checked={c.case_insensitive ?? true}
                      onChange={(e) =>
                        updateCondition(i, { case_insensitive: e.target.checked })
                      }
                    />
                    ignore case
                  </label>
                  {conditions.length > 1 && (
                    <button
                      type="button"
                      className="cond-row__rm"
                      aria-label={`Remove condition ${i + 1}`}
                      onClick={() => removeCondition(i)}
                    >
                      ✕
                    </button>
                  )}
                  {overMatch && (
                    <p className="cond-row__warn">
                      ! &ldquo;{c.value.trim()}&rdquo; is very short — &ldquo;contains&rdquo;
                      will match broadly. Consider a longer fragment, or switch to
                      &ldquo;starts with&rdquo; / &ldquo;is exactly&rdquo;.
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="cond-actions">
            <button
              type="button"
              className="v6-scope-chip-add"
              onClick={addCondition}
              disabled={conditions.length >= MAX_MATCH_CONDITIONS_PER_AXIS}
            >
              + add condition
            </button>
          </div>

          {predicateErrors.length > 0 && (
            <div className="v6-validation-list" style={{ marginTop: 8 }}>
              {predicateErrors.map((e, i) => (
                <div key={i} className="vrow warn">
                  <span className="vico">!</span>
                  {e}
                </div>
              ))}
            </div>
          )}

          {previewPending && (
            <div className="cond-preview-pending" role="status" aria-live="polite">
              <span className="cond-spinner" aria-hidden="true" />
              <span>
                {devicePreview
                  ? "Updating matched cameras…"
                  : "Finding matching cameras…"}
              </span>
            </div>
          )}

          {devicePreview && (
            <div className={previewPending ? "cond-preview-stale" : undefined}>
              <DevicePreviewPanel preview={devicePreview} conditions={conditions} />
            </div>
          )}

          <label
            className="rule-wiz__predicate-ack"
            style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "flex-start" }}
          >
            <input
              type="checkbox"
              aria-label="acknowledge dynamic set"
              checked={acknowledged}
              onChange={(e) => onAcknowledgedChange(e.target.checked)}
            />
            <span>
              I understand this is a <b>live set</b>: cameras are matched by name
              at access time, so newly added or renamed cameras are included (or
              excluded) automatically — not just the ones shown in the preview.
            </span>
          </label>
        </div>
      )}
    </details>
  );
}

// ---------------------------------------------------------------------------
// <DevicePreviewPanel> — surfaces the predicate match summary from the live
// preview (LLD 12 §4.4). Truncation is a BLOCKING warning, never authoritative.
// ---------------------------------------------------------------------------

function DevicePreviewPanel(props: {
  preview: PreviewDeviceSummary;
  conditions: MatchCondition[];
}): JSX.Element {
  const { preview, conditions } = props;
  const matched = preview.matched_devices.length;
  return (
    <div className="v6-review-block cond-preview" style={{ marginTop: 10 }}>
      <div className="cond-preview__bar">
        <span className="cond-preview__count">{matched}</span>
        <span className="cond-preview__of">
          of {preview.scanned_device_count} cameras matched
        </span>
      </div>
      {matched > 0 && (
        <div className="cond-fleet">
          {preview.matched_devices.slice(0, 20).map((d) => (
            <div key={d.doorbot_id} className="cond-dev">
              <span className="cond-dev__dot" />
              <span className="cond-dev__nm">
                {d.name
                  ? highlightName(d.name, conditions).map((seg, idx) =>
                      seg.match ? (
                        <mark key={idx}>{seg.text}</mark>
                      ) : (
                        <span key={idx}>{seg.text}</span>
                      ),
                    )
                  : d.doorbot_id}
              </span>
            </div>
          ))}
          {matched > 20 && (
            <div className="cond-fleet__more">
              + {matched - 20} more matching camera
              {matched - 20 === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}
      {preview.truncated && (
        <div className="v6-validation-list" style={{ marginTop: 8 }}>
          <div className="vrow warn">
            <span className="vico">!</span>
            The fleet was too large to scan completely — this preview is a
            partial, point-in-time estimate and is <b>not authoritative</b>.
            The rule still applies to every matching camera at access time.
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <ReviewBlock>
// ---------------------------------------------------------------------------

interface ReviewBlockProps {
  groupName: string;
  groupSummary: GroupSummary | undefined;
  role: Role | null;
  roleSlot: string;
  whereSummary: string;
  locationsCount: number;
  locMode: ScopeMode;
  locIds: string[];
  validateResp: { ok: boolean; errors?: string[] } | null;
  validating: boolean;
  groupExists: boolean;
}

function ReviewBlock(props: ReviewBlockProps): JSX.Element {
  const {
    groupName,
    groupSummary,
    role,
    roleSlot,
    whereSummary,
    locationsCount,
    locMode,
    locIds,
    validateResp,
    validating,
    groupExists,
  } = props;

  const userCount = groupSummary?.member_count ?? 0;
  const camerasInScope =
    locMode === "ALL"
      ? "all"
      : locIds.length === 0
        ? "0"
        : `${locIds.length} loc${locIds.length === 1 ? "" : "s"}`;
  const conflicts =
    validateResp && validateResp.errors
      ? validateResp.errors.length
      : 0;

  type VRow = { tone: "ok" | "info" | "warn"; text: string };
  const rows: VRow[] = [];
  if (groupExists && groupSummary) {
    rows.push({
      tone: "ok",
      text: `Group exists and has ${userCount} member${userCount === 1 ? "" : "s"}`,
    });
  } else if (groupName) {
    rows.push({
      tone: "warn",
      text: `Group "${groupName}" has not synced yet — rule will activate once it does`,
    });
  } else {
    rows.push({ tone: "warn", text: "No group selected" });
  }
  if (role) {
    rows.push({ tone: "ok", text: "Role exists and is enabled" });
  } else {
    rows.push({ tone: "warn", text: "No role selected" });
  }
  if (validating) {
    rows.push({ tone: "info", text: "Checking for conflicts…" });
  } else if (validateResp && !validateResp.ok && validateResp.errors) {
    for (const e of validateResp.errors) {
      rows.push({ tone: "warn", text: e });
    }
  } else if (validateResp && validateResp.ok) {
    rows.push({
      tone: "ok",
      text: "No conflicting rule on this group at these locations",
    });
  }
  if (groupSummary && groupSummary.rules_count > 0) {
    rows.push({
      tone: "info",
      text: `This group already has ${groupSummary.rules_count} other rule${groupSummary.rules_count === 1 ? "" : "s"} — they will compose, not replace`,
    });
  }

  return (
    <div className="v6-review-block">
      <div className="v6-english-panel">
        You're about to grant <b>{userCount} user{userCount === 1 ? "" : "s"}</b>{" "}
        in <b>{groupName || "(no group)"}</b> the{" "}
        <b>{roleSlot || "(no role)"}</b> role at <b>{whereSummary}</b>.
      </div>

      <div className="v6-impact-row">
        <div>
          <div className="impact-num">{userCount}</div>
          <div className="impact-label">users gain access</div>
        </div>
        <div>
          <div className="impact-num">
            {camerasInScope === "all" ? locationsCount : camerasInScope}
          </div>
          <div className="impact-label">
            {camerasInScope === "all" ? "locations in scope" : "scope size"}
          </div>
        </div>
        <div>
          <div className="impact-num">{conflicts}</div>
          <div className="impact-label">conflicts</div>
        </div>
      </div>

      <div className="v6-validation-list">
        {rows.map((r, i) => (
          <div key={i} className={`vrow ${r.tone}`}>
            <span className="vico">
              {r.tone === "ok" ? "✓" : r.tone === "warn" ? "!" : "i"}
            </span>
            {r.text}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// <CustomRoleQuickModal> — quick "name + permission group toggles" form
// ---------------------------------------------------------------------------

interface CustomRoleModalProps {
  catalog: PermissionCatalog | null;
  onClose: () => void;
  onCreated: (role: Role) => void;
  onError: (err: unknown) => void;
}

function CustomRoleQuickModal(props: CustomRoleModalProps): JSX.Element {
  const { catalog, onClose, onCreated, onError } = props;
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const toggleGroup = (groupKey: string): void => {
    if (!catalog) return;
    const grp = catalog.groups.find((g) => g.key === groupKey);
    if (!grp) return;
    const next = new Set(selected);
    const allOn = grp.permissions.every(
      (p) => p.is_reserved || next.has(p.key),
    );
    for (const p of grp.permissions) {
      if (p.is_reserved) continue;
      if (allOn) next.delete(p.key);
      else next.add(p.key);
    }
    setSelected(next);
  };

  const handleCreate = useCallback(async () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    setSubmitting(true);
    try {
      const created = await roleClient.create({
        role_id: slugify(trimmed) || "custom-role",
        display_name: trimmed,
        description: "Created from rule wizard",
        permission_set: Array.from(selected),
      });
      onCreated(created);
    } catch (err) {
      onError(err);
    } finally {
      setSubmitting(false);
    }
  }, [name, selected, onCreated, onError]);

  return (
    <Modal
      open
      onClose={onClose}
      title="Build a custom role"
      description="Pick the permission groups this role should include. You can refine the exact permissions later in Roles."
      variant="detail"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={name.trim().length === 0 || selected.size === 0}
            onClick={() => void handleCreate()}
          >
            Create role
          </Button>
        </>
      }
    >
      <label className="rule-wiz__adv-label">Role name</label>
      <input
        className="rule-wiz__adv-input"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Site Investigator"
      />
      <div style={{ marginTop: 14 }}>
        {catalog ? (
          catalog.groups.map((grp) => {
            const allOn = grp.permissions
              .filter((p) => !p.is_reserved)
              .every((p) => selected.has(p.key));
            return (
              <label
                key={grp.key}
                className="rule-wiz__perm-group"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 0",
                  borderBottom: "1px dashed var(--ring-tertiary-border)",
                }}
              >
                <input
                  type="checkbox"
                  checked={allOn}
                  onChange={() => toggleGroup(grp.key)}
                />
                <span style={{ fontWeight: 600 }}>{grp.label}</span>
                <span className="rule-wiz__adv-hint">
                  {grp.permissions.length} permission
                  {grp.permissions.length === 1 ? "" : "s"}
                </span>
              </label>
            );
          })
        ) : (
          <p className="rule-wiz__adv-hint">Loading permission catalog…</p>
        )}
      </div>
    </Modal>
  );
}
